// src/components/OnboardingWizard.tsx
// Asistente de primeros pasos de un profesional nuevo: su primer servicio, sus
// días y horarios, y su link para compartir.
//
// Lavada de cara oct-2026 (con publicidad en Instagram trayendo gente nueva).
// Lo que se arregló en el camino, probado de punta a punta contra una base
// temporal:
//  - el precio "15.000" se guardaba como $15 (input numérico + parseInt);
//  - volver al paso 1 y avanzar creaba el servicio otra vez (ahora lo edita);
//  - los horarios mandaban 15 min de pausa entre turnos sin decirlo: con una
//    consulta de 1 h la página ofrecía 9:00, 10:15, 11:30... Ahora va sin
//    pausa, como la tienen todos los que usan la agenda; se cambia desde
//    Configuración;
//  - el paso de pagos tenía dos "Saltar" (uno cerraba todo el asistente y la
//    persona nunca veía su link) y "Conectar MercadoPago" la sacaba de la
//    pantalla a mitad de camino. El cobro arranca en "se paga en el
//    consultorio" y la seña se activa después, desde Pagos;
//  - en el celular el encabezado del sitio (z-100) tapaba el del asistente.

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, ChevronLeft, ChevronRight, Copy, ExternalLink, Plus, X } from 'lucide-react';
import { authFetch } from '../services/api';
import { FilaDeDia, errorDelDia } from './MarcaUI';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface OnboardingWizardProps {
  userId: number;
  /**
   * Slug público, para armar el link lindo (consultarapp.com/p/nombre-apellido).
   * Sin él quedaba /p/96, que es el primer link que ve un profesional nuevo y
   * el que termina pegando en su bio de Instagram.
   */
  slug?: string | null;
  userName: string;
  userType: 'salud' | 'belleza';
  especialidad: string;
  onComplete: () => void;
  onSkip: () => void;
}

type ModoPrecio = 'fijo' | 'obra_social' | 'consultar';

interface Sugerencia {
  nombre: string;
  duracion: number;
  categoria: string;
}

interface Dia {
  clave: string;
  nombre: string;
  atiende: boolean;
  desde: string;
  hasta: string;
  corte: boolean;
  corteDesde: string;
  corteHasta: string;
}

const DURACIONES = [15, 20, 30, 40, 45, 50, 60, 90, 120];

// Las mismas de la pantalla de Servicios (ProfessionalServicesConfig): si el
// asistente guardara otra, al editar el servicio allá no aparecería.
const CATEGORIAS: Record<'salud' | 'belleza', string[]> = {
  salud: ['Consulta', 'Control', 'Terapia', 'Especialidad', 'Pediatría', 'Psicología', 'Urgencia', 'Otro'],
  belleza: ['Pestañas', 'Cejas', 'Uñas', 'Cabello', 'Depilación', 'Estética Facial', 'Maquillaje', 'Masajes', 'Otro'],
};

// Sugerencias por especialidad. Por palabra y no por nombre exacto: antes solo
// cinco especialidades tenían las suyas (y "Pestañas", que no existe en el
// registro, no le tocaba a nadie). Sin precio a propósito: el de cada uno lo
// sabe cada uno, y uno sugerido se guardaba sin mirarlo.
const SUGERENCIAS_SALUD: { patron: RegExp; lista: Sugerencia[] }[] = [
  { patron: /odont|ortodon|endodon|periodon|bucal/i, lista: [
    { nombre: 'Consulta odontológica', duracion: 30, categoria: 'Consulta' },
    { nombre: 'Limpieza dental', duracion: 45, categoria: 'Especialidad' },
    { nombre: 'Control', duracion: 20, categoria: 'Control' },
  ] },
  { patron: /psicopedag/i, lista: [
    { nombre: 'Sesión de psicopedagogía', duracion: 45, categoria: 'Terapia' },
    { nombre: 'Evaluación inicial', duracion: 60, categoria: 'Consulta' },
  ] },
  { patron: /psicolog|psiquiat|neuropsico/i, lista: [
    { nombre: 'Sesión de terapia', duracion: 50, categoria: 'Psicología' },
    { nombre: 'Primera entrevista', duracion: 60, categoria: 'Psicología' },
  ] },
  { patron: /kinesio|fisiot|terapia ocup|fonoaud/i, lista: [
    { nombre: 'Sesión', duracion: 45, categoria: 'Terapia' },
    { nombre: 'Evaluación inicial', duracion: 60, categoria: 'Consulta' },
  ] },
  { patron: /nutri/i, lista: [
    { nombre: 'Primera consulta nutricional', duracion: 60, categoria: 'Consulta' },
    { nombre: 'Control nutricional', duracion: 30, categoria: 'Control' },
  ] },
  { patron: /pediatr|neonat/i, lista: [
    { nombre: 'Consulta pediátrica', duracion: 30, categoria: 'Pediatría' },
    { nombre: 'Control de niño sano', duracion: 30, categoria: 'Control' },
  ] },
  { patron: /flebolog|vascular/i, lista: [
    { nombre: 'Consulta flebológica', duracion: 30, categoria: 'Consulta' },
    { nombre: 'Escleroterapia', duracion: 30, categoria: 'Especialidad' },
    { nombre: 'Eco Doppler', duracion: 30, categoria: 'Especialidad' },
  ] },
  { patron: /acupunt|ayurveda|homeop|naturop|medicina china/i, lista: [
    { nombre: 'Sesión', duracion: 60, categoria: 'Terapia' },
    { nombre: 'Primera consulta', duracion: 60, categoria: 'Consulta' },
  ] },
];

const SUGERENCIAS_BELLEZA: { patron: RegExp; lista: Sugerencia[] }[] = [
  { patron: /pesta|lash/i, lista: [
    { nombre: 'Lifting de pestañas', duracion: 60, categoria: 'Pestañas' },
    { nombre: 'Extensiones de pestañas', duracion: 120, categoria: 'Pestañas' },
    { nombre: 'Retoque de extensiones', duracion: 60, categoria: 'Pestañas' },
  ] },
  { patron: /ceja|microblading/i, lista: [
    { nombre: 'Diseño de cejas', duracion: 30, categoria: 'Cejas' },
    { nombre: 'Laminado de cejas', duracion: 45, categoria: 'Cejas' },
  ] },
  { patron: /uña|manicur|nail|esmaltado/i, lista: [
    { nombre: 'Esmaltado semipermanente', duracion: 60, categoria: 'Uñas' },
    { nombre: 'Uñas esculpidas', duracion: 90, categoria: 'Uñas' },
    { nombre: 'Manicura', duracion: 45, categoria: 'Uñas' },
  ] },
  { patron: /pelu|color|barber|cabello|alisad|peinad/i, lista: [
    { nombre: 'Corte', duracion: 45, categoria: 'Cabello' },
    { nombre: 'Color', duracion: 120, categoria: 'Cabello' },
    { nombre: 'Peinado', duracion: 60, categoria: 'Cabello' },
  ] },
  { patron: /facial|limpieza|peeling|hidraf|radiofrec|microderm/i, lista: [
    { nombre: 'Limpieza facial', duracion: 60, categoria: 'Estética Facial' },
    { nombre: 'Peeling', duracion: 45, categoria: 'Estética Facial' },
  ] },
  { patron: /maquill/i, lista: [
    { nombre: 'Maquillaje social', duracion: 60, categoria: 'Maquillaje' },
    { nombre: 'Maquillaje de novia', duracion: 90, categoria: 'Maquillaje' },
  ] },
  { patron: /depil/i, lista: [
    { nombre: 'Depilación zona chica', duracion: 20, categoria: 'Depilación' },
    { nombre: 'Depilación piernas completas', duracion: 45, categoria: 'Depilación' },
  ] },
  { patron: /masaje|drenaje|presoter|reductor|cavitac/i, lista: [
    { nombre: 'Masaje descontracturante', duracion: 60, categoria: 'Masajes' },
    { nombre: 'Drenaje linfático', duracion: 60, categoria: 'Masajes' },
  ] },
  { patron: /micropig|tatua/i, lista: [
    { nombre: 'Micropigmentación', duracion: 120, categoria: 'Otro' },
    { nombre: 'Retoque', duracion: 60, categoria: 'Otro' },
  ] },
];

const sugerenciasPara = (tipo: 'salud' | 'belleza', especialidad: string): Sugerencia[] => {
  const tabla = tipo === 'salud' ? SUGERENCIAS_SALUD : SUGERENCIAS_BELLEZA;
  const encontrada = tabla.find((s) => s.patron.test(especialidad || ''));
  if (encontrada) return encontrada.lista;
  if (tipo === 'belleza') {
    return [
      { nombre: 'Primera sesión', duracion: 60, categoria: 'Otro' },
      { nombre: 'Sesión', duracion: 45, categoria: 'Otro' },
    ];
  }
  const conEspecialidad = especialidad && especialidad !== 'Profesional'
    ? `Consulta de ${especialidad.toLowerCase()}`
    : 'Consulta';
  return [
    { nombre: conEspecialidad, duracion: 30, categoria: 'Consulta' },
    { nombre: 'Primera consulta', duracion: 45, categoria: 'Consulta' },
    { nombre: 'Control', duracion: 20, categoria: 'Control' },
  ];
};

// Cada 15 minutos alcanza para arrancar (9:30, 12:45...); más fino se ajusta
// desde la configuración de horarios.
const HORAS: string[] = (() => {
  const lista: string[] = [];
  for (let m = 6 * 60; m <= 23 * 60; m += 15) {
    lista.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  }
  return lista;
})();

const DIAS_INICIALES: Dia[] = [
  { clave: 'monday', nombre: 'Lunes', atiende: true, desde: '09:00', hasta: '18:00', corte: false, corteDesde: '13:00', corteHasta: '16:00' },
  { clave: 'tuesday', nombre: 'Martes', atiende: true, desde: '09:00', hasta: '18:00', corte: false, corteDesde: '13:00', corteHasta: '16:00' },
  { clave: 'wednesday', nombre: 'Miércoles', atiende: true, desde: '09:00', hasta: '18:00', corte: false, corteDesde: '13:00', corteHasta: '16:00' },
  { clave: 'thursday', nombre: 'Jueves', atiende: true, desde: '09:00', hasta: '18:00', corte: false, corteDesde: '13:00', corteHasta: '16:00' },
  { clave: 'friday', nombre: 'Viernes', atiende: true, desde: '09:00', hasta: '18:00', corte: false, corteDesde: '13:00', corteHasta: '16:00' },
  { clave: 'saturday', nombre: 'Sábado', atiende: false, desde: '09:00', hasta: '13:00', corte: false, corteDesde: '11:00', corteHasta: '12:00' },
  { clave: 'sunday', nombre: 'Domingo', atiende: false, desde: '09:00', hasta: '13:00', corte: false, corteDesde: '11:00', corteHasta: '12:00' },
];

const textoDuracion = (min: number) => {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const resto = min % 60;
  return resto ? `${h} h ${resto}` : `${h} h`;
};

// "20.000" a mano: toLocaleString depende del navegador y en alguno sale "20,000"
const conPuntos = (n: number) => (n > 0 ? String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.') : '');

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  userId,
  slug,
  userName,
  userType,
  especialidad,
  onComplete,
  onSkip
}) => {
  // El identificador público: el nombre si lo hay, el número si no.
  const linkPublico = `consultarapp.com/p/${slug || userId}`;
  const linkCompleto = `https://${linkPublico}`;
  const quienes = userType === 'salud' ? 'tus pacientes' : 'tus clientes';
  const sugerencias = sugerenciasPara(userType, especialidad);

  const [paso, setPaso] = useState<1 | 2 | 3>(1);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Paso 1: el servicio. `servicioId` existe desde que se guardó una vez: si
  // vuelve atrás y cambia algo, se edita ese mismo en vez de crear otro.
  const [servicioId, setServicioId] = useState<number | null>(null);
  const [nombre, setNombre] = useState('');
  const [duracion, setDuracion] = useState(sugerencias[0]?.duracion || 30);
  const [modoPrecio, setModoPrecio] = useState<ModoPrecio>('fijo');
  const [precio, setPrecio] = useState(0);
  const [categoria, setCategoria] = useState(sugerencias[0]?.categoria || CATEGORIAS[userType][0]);
  const [descripcion, setDescripcion] = useState('');
  const [conDescripcion, setConDescripcion] = useState(false);
  const [faltaNombre, setFaltaNombre] = useState(false);
  const [faltaPrecio, setFaltaPrecio] = useState(false);

  // Paso 2: los días
  const [dias, setDias] = useState<Dia[]>(DIAS_INICIALES);
  const [revisarDias, setRevisarDias] = useState(false);

  // Paso 3: compartir
  const [copiado, setCopiado] = useState(false);
  const [avisoInstagram, setAvisoInstagram] = useState(false);

  // Sin scroll de la página de atrás mientras está abierto
  useEffect(() => {
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previo; };
  }, []);

  const elegirSugerencia = (s: Sugerencia) => {
    setNombre(s.nombre);
    setDuracion(s.duracion);
    setCategoria(s.categoria);
    setFaltaNombre(false);
  };

  const cambiarDia = (i: number, cambios: Partial<Dia>) => {
    setDias((prev) => prev.map((d, j) => (j === i ? { ...d, ...cambios } : d)));
  };

  const primerDia = dias.find((d) => d.atiende);
  const hayHorariosDistintos = !!primerDia && dias.some((d) => d.atiende && (
    d.desde !== primerDia.desde || d.hasta !== primerDia.hasta || d.corte !== primerDia.corte
    || (d.corte && (d.corteDesde !== primerDia.corteDesde || d.corteHasta !== primerDia.corteHasta))
  ));
  const copiarATodos = () => {
    if (!primerDia) return;
    const { desde, hasta, corte, corteDesde, corteHasta } = primerDia;
    setDias((prev) => prev.map((d) => (d.atiende ? { ...d, desde, hasta, corte, corteDesde, corteHasta } : d)));
  };

  // ===== Guardar =====

  const guardarServicio = async () => {
    const cuerpo = {
      name: nombre.trim(),
      description: descripcion.trim(),
      duration: duracion,
      price: modoPrecio === 'fijo' ? precio : 0,
      category: categoria,
      priceMode: modoPrecio,
      isActive: true,
    };
    const base = `${API_URL}/api/services/professional/${userId}/services`;
    const r = await authFetch(servicioId ? `${base}/${servicioId}` : base, {
      method: servicioId ? 'PUT' : 'POST',
      body: JSON.stringify(cuerpo),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.message || data.error || 'No pudimos guardar el servicio.');
    if (!servicioId && data.service?.id) setServicioId(data.service.id);
  };

  // Red de seguridad: las cuentas creadas antes del arreglo del servidor
  // quedaban en "consulta_completa" (el default de la tabla) sin alias ni
  // MercadoPago, y su página mandaba a pagar sin datos para pagar. Solo toca
  // ese caso: una forma de cobro elegida a propósito no se pisa.
  const asegurarCobroEnConsultorio = async () => {
    try {
      const r = await fetch(`${API_URL}/api/payments/config/${userId}`);
      if (!r.ok) return;
      const { data } = await r.json();
      if (data?.tipo_cobro === 'consulta_completa' && !data.alias_cbu && !data.acepta_mercadopago) {
        await authFetch(`${API_URL}/api/payments/config`, {
          method: 'PUT',
          body: JSON.stringify({
            tipo_cobro: 'efectivo',
            porcentaje_sena: data.porcentaje_sena ?? 30,
            acepta_efectivo: true,
            acepta_transferencia: false,
            acepta_mercadopago: false,
            alias_cbu: null,
            titular_cuenta: data.titular_cuenta ?? null,
          }),
        });
      }
    } catch {
      // No frena el asistente: se puede ajustar desde Pagos
    }
  };

  const guardarHorarios = async () => {
    const r = await authFetch(`${API_URL}/api/schedule/professional/${userId}/config`, {
      method: 'POST',
      body: JSON.stringify({
        workingDays: dias.map((d) => ({
          day: d.clave,
          dayName: d.nombre,
          isWorking: d.atiende,
          startTime: d.desde,
          endTime: d.hasta,
          hasBreak: d.atiende && d.corte,
          breakStart: d.atiende && d.corte ? d.corteDesde : null,
          breakEnd: d.atiende && d.corte ? d.corteHasta : null,
        })),
        appointmentDuration: duracion,
        // Sin pausa entre turnos (antes 15, sin avisar: ver arriba)
        bufferTime: 0,
        maxAdvanceBooking: 180,
        allowWeekendBooking: dias.some((d) => d.atiende && (d.clave === 'saturday' || d.clave === 'sunday')),
        automaticConfirmation: true,
        timeZone: 'America/Argentina/Buenos_Aires',
      }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || data.message || 'No pudimos guardar los horarios.');
    await asegurarCobroEnConsultorio();
  };

  const siguiente = async () => {
    setError(null);
    if (paso === 1) {
      const sinNombre = !nombre.trim();
      const sinPrecio = modoPrecio === 'fijo' && !(precio > 0);
      setFaltaNombre(sinNombre);
      setFaltaPrecio(sinPrecio);
      if (sinNombre || sinPrecio) return;
    }
    if (paso === 2) {
      setRevisarDias(true);
      if (!dias.some((d) => d.atiende) || dias.some((d) => errorDelDia(d))) return;
    }
    setGuardando(true);
    try {
      if (paso === 1) await guardarServicio();
      else await guardarHorarios();
      setPaso(paso === 1 ? 2 : 3);
    } catch (e: any) {
      setError(e?.message || 'Algo salió mal. Probá de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(linkCompleto);
    } catch {
      // Navegadores viejos o sin permiso: el método de siempre
      const t = document.createElement('textarea');
      t.value = linkCompleto;
      document.body.appendChild(t);
      t.select();
      try { document.execCommand('copy'); } catch { /* se ve igual en pantalla */ }
      document.body.removeChild(t);
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  const titulo = paso === 1 ? 'Tu primer servicio' : paso === 2 ? 'Tus días y horarios' : '¡Tu página está lista!';
  const bajada = paso === 1
    ? `Hola ${userName}: en dos minutos dejás tu página lista para recibir turnos.`
    : paso === 2
      ? `${quienes.charAt(0).toUpperCase()}${quienes.slice(1)} van a poder reservar solo en estos horarios.`
      : `Ya te pueden reservar. Pasale el link a ${quienes}.`;

  const ficha = 'marca-ficha rounded-xl px-3 py-2 text-sm transition-colors';
  const etiqueta = 'block text-sm font-semibold text-gray-800 mb-2';
  const sinDias = revisarDias && !dias.some((d) => d.atiende);

  // Directo en <body>: el panel lo monta dentro de un contenedor con
  // `space-y-6`, que le daba 24 px de margen arriba a este bloque fijo. Así
  // quedaba corrido hacia abajo y el encabezado del sitio le tapaba la parte
  // de arriba en el celular (también al asistente viejo).
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[120] sm:flex sm:items-center sm:justify-center sm:p-4">
      <div className="absolute inset-0 bg-slate-900/55 animate-backdrop-in" aria-hidden="true" />

      {/* En el celular ocupa toda la pantalla; en la computadora, una tarjeta.
          text-left: la app centra todo por defecto (.App de CRA). */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="asistente-titulo"
        className={`marca marca--${userType} absolute inset-0 sm:relative sm:inset-auto sm:w-full sm:max-w-lg sm:max-h-[92vh] sm:rounded-3xl bg-white shadow-2xl flex flex-col overflow-hidden text-left animate-slide-up`}
      >
        {/* Encabezado en arco, con el progreso */}
        <div
          className="marca-portada relative shrink-0 px-5 pb-10 text-white"
          style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}
        >
          <span className="marca-mancha marca-mancha--1" />
          <span className="marca-mancha marca-mancha--2" />
          <div className="relative flex items-center justify-between">
            <span className="text-xs font-semibold bg-white/20 rounded-full px-2.5 py-1">
              {paso < 3 ? `Paso ${paso} de 2` : '¡Listo!'}
            </span>
            {paso < 3 && (
              <button
                type="button"
                onClick={onSkip}
                className="text-xs font-medium text-white/85 hover:text-white underline-offset-2 hover:underline"
              >
                Lo hago después
              </button>
            )}
          </div>
          <h2 id="asistente-titulo" className="relative mt-3 text-2xl font-bold leading-tight">{titulo}</h2>
          <p className="relative mt-1 text-sm text-white/90 leading-snug">{bajada}</p>
          <div className="relative mt-4 flex gap-1.5" aria-hidden="true">
            {[1, 2].map((n) => (
              <span key={n} className={`h-1 flex-1 rounded-full ${n <= paso ? 'bg-white' : 'bg-white/35'}`} />
            ))}
          </div>
        </div>

        {/* Contenido del paso (se monta de nuevo en cada paso: arranca arriba) */}
        <div key={paso} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 pt-3 pb-6 animate-panel-in">
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
              <X className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {paso === 1 && (
            <div className="space-y-5">
              <div>
                <p className={etiqueta}>Sugerencias para {especialidad || 'vos'}</p>
                <div className="flex flex-wrap gap-2">
                  {sugerencias.map((s) => (
                    <button
                      key={s.nombre}
                      type="button"
                      aria-pressed={nombre === s.nombre}
                      onClick={() => elegirSugerencia(s)}
                      className={ficha}
                    >
                      {s.nombre} · {textoDuracion(s.duracion)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="asistente-nombre" className={etiqueta}>Nombre del servicio</label>
                <input
                  id="asistente-nombre"
                  type="text"
                  value={nombre}
                  onChange={(e) => { setNombre(e.target.value); setFaltaNombre(false); }}
                  placeholder="Ej: Consulta"
                  className="marca-campo w-full rounded-xl border border-gray-300 px-4 py-3 text-base"
                />
                {faltaNombre && <p className="mt-1.5 text-xs text-red-600">Poné un nombre (o elegí una sugerencia).</p>}
              </div>

              <div>
                <p className={etiqueta}>Duración</p>
                <div className="flex flex-wrap gap-2">
                  {DURACIONES.map((m) => (
                    <button key={m} type="button" aria-pressed={duracion === m} onClick={() => setDuracion(m)} className={ficha}>
                      {textoDuracion(m)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className={etiqueta}>Precio</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {([
                    ['fijo', 'Precio fijo'],
                    ...(userType === 'salud' ? [['obra_social', 'Obra social']] : []),
                    ['consultar', 'A consultar'],
                  ] as [ModoPrecio, string][]).map(([valor, texto]) => (
                    <button
                      key={valor}
                      type="button"
                      aria-pressed={modoPrecio === valor}
                      onClick={() => { setModoPrecio(valor); setFaltaPrecio(false); }}
                      className={ficha}
                    >
                      {texto}
                    </button>
                  ))}
                </div>
                {modoPrecio === 'fijo' ? (
                  <>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-base">$</span>
                      {/* Texto y no número: "15.000" en un input numérico
                          quedaba en 15 (el punto es el decimal). */}
                      <input
                        type="text"
                        inputMode="numeric"
                        aria-label="Precio en pesos"
                        value={conPuntos(precio)}
                        onChange={(e) => {
                          setPrecio(Number(e.target.value.replace(/\D/g, '').slice(0, 9)) || 0);
                          setFaltaPrecio(false);
                        }}
                        placeholder="20.000"
                        className="marca-campo w-full rounded-xl border border-gray-300 pl-8 pr-4 py-3 text-base"
                      />
                    </div>
                    {faltaPrecio && <p className="mt-1.5 text-xs text-red-600">Poné el precio, o elegí "A consultar".</p>}
                  </>
                ) : (
                  <p className="text-sm text-gray-600">
                    {modoPrecio === 'obra_social'
                      ? 'En tu página va a decir "Obra social" en lugar del precio.'
                      : 'En tu página va a decir "A consultar" en lugar del precio.'}
                  </p>
                )}
              </div>

              <div>
                <p className={etiqueta}>Categoría</p>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIAS[userType].map((c) => (
                    <button key={c} type="button" aria-pressed={categoria === c} onClick={() => setCategoria(c)} className={ficha}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {conDescripcion ? (
                <div>
                  <label htmlFor="asistente-descripcion" className={etiqueta}>Descripción</label>
                  <textarea
                    id="asistente-descripcion"
                    value={descripcion}
                    onChange={(e) => setDescripcion(e.target.value)}
                    rows={3}
                    placeholder="Qué incluye, cómo prepararse, etc."
                    className="marca-campo w-full rounded-xl border border-gray-300 px-4 py-3 text-base"
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConDescripcion(true)}
                  className="marca-acento inline-flex items-center gap-1 text-sm font-semibold"
                >
                  <Plus className="w-4 h-4" /> Agregar una descripción (opcional)
                </button>
              )}
            </div>
          )}

          {paso === 2 && (
            <div className="space-y-2.5">
              {/* La misma fila de la pantalla de Horarios (MarcaUI) */}
              {dias.map((d, i) => (
                <FilaDeDia
                  key={d.clave}
                  dia={d}
                  horas={HORAS}
                  onCambio={(c) => cambiarDia(i, c)}
                  problema={revisarDias ? errorDelDia(d) : null}
                />
              ))}

              {sinDias && <p className="text-sm text-red-600">Elegí al menos un día.</p>}

              {hayHorariosDistintos && primerDia && (
                <button
                  type="button"
                  onClick={copiarATodos}
                  className="marca-acento w-full rounded-xl border border-dashed border-current px-3 py-2.5 text-sm font-semibold"
                >
                  Usar el horario del {primerDia.nombre.toLowerCase()} en todos los días
                </button>
              )}
            </div>
          )}

          {paso === 3 && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <div className="marca-tilde marca-boton w-12 h-12 rounded-2xl flex items-center justify-center shrink-0">
                  <Check className="w-6 h-6" />
                </div>
                <p className="text-sm text-gray-600">
                  Guardamos <strong className="text-gray-900">{nombre.trim()}</strong> y tus horarios. Ya aparecen en tu página.
                </p>
              </div>

              <div className="marca-tarjeta border rounded-2xl p-4">
                <p className="marca-acento text-xs font-semibold uppercase tracking-wide">Tu link de reservas</p>
                <p className="mt-1 text-base font-semibold text-gray-900 break-all">{linkPublico}</p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={copiarLink}
                    className="marca-boton rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5"
                  >
                    {copiado ? <><Check className="w-4 h-4" /> Copiado</> : <><Copy className="w-4 h-4" /> Copiar link</>}
                  </button>
                  <a
                    href={linkCompleto}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-xl py-2.5 text-sm font-semibold border border-gray-300 bg-white text-gray-800 flex items-center justify-center gap-1.5"
                  >
                    <ExternalLink className="w-4 h-4" /> Ver mi página
                  </a>
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-900 mb-2">Compartilo</p>
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`Reservá tu turno conmigo en ${linkCompleto}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-xl py-2.5 text-sm font-semibold bg-green-500 text-white flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    WhatsApp
                  </a>
                  {/* Instagram no recibe links desde afuera (no hay un "compartir"
                      por URL como el de WhatsApp): copia el link y dice dónde
                      pegarlo, que es lo único honesto que se puede ofrecer. */}
                  <button
                    type="button"
                    onClick={() => {
                      copiarLink();
                      setAvisoInstagram(true);
                    }}
                    className="rounded-xl py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                    Instagram
                  </button>
                </div>
                {avisoInstagram && (
                  <p className="mt-2 rounded-xl border border-purple-200 bg-purple-50 p-3 text-sm text-purple-800">
                    Link copiado. En Instagram andá a <strong>Editar perfil</strong> y pegalo en <strong>Enlaces</strong>: así te queda fijo en la bio.
                  </p>
                )}
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-900 mb-2">Cuando quieras, desde tu panel</p>
                {/* El texto de cada renglón va en su propio <span>: suelto, cada
                    pedazo entre negritas era una columna del flex. */}
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex gap-2"><span className="marca-acento font-bold">·</span><span>Sumar más servicios (en <strong>Servicios</strong>).</span></li>
                  <li className="flex gap-2"><span className="marca-acento font-bold">·</span><span>Pedir seña por transferencia o MercadoPago (en <strong>Pagos</strong>). Por ahora se paga en el consultorio.</span></li>
                  {userType === 'salud'
                    ? <li className="flex gap-2"><span className="marca-acento font-bold">·</span><span>Cargar tu matrícula para hacer recetas electrónicas.</span></li>
                    : <li className="flex gap-2"><span className="marca-acento font-bold">·</span><span>Subir fotos de tu trabajo al Portfolio.</span></li>}
                  <li className="flex gap-2"><span className="marca-acento font-bold">·</span><span>Sumar a tu secretaria con su propio usuario.</span></li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Pie con la acción del paso */}
        <div
          className="shrink-0 border-t border-gray-100 bg-white px-5 pt-3 flex items-center gap-3"
          style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
        >
          {paso === 2 && (
            <button
              type="button"
              onClick={() => { setError(null); setPaso(1); }}
              disabled={guardando}
              className="flex items-center gap-1 rounded-xl px-3 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-100"
            >
              <ChevronLeft className="w-4 h-4" /> Atrás
            </button>
          )}
          {paso < 3 ? (
            <button
              type="button"
              onClick={siguiente}
              disabled={guardando}
              className="marca-boton flex-1 flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-base font-semibold shadow-sm"
            >
              {guardando ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Guardando…
                </>
              ) : (
                <>
                  {paso === 1 ? 'Siguiente' : 'Guardar horarios'}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={onComplete}
              className="marca-boton flex-1 rounded-xl px-5 py-3 text-base font-semibold shadow-sm"
            >
              Ir a mi panel
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OnboardingWizard;
